import {
	WebPushService,
	type WebPushTargetModel,
} from '@supply-mover-2-0/web-ui';
import {
	getPortalPushPublicKey,
	registerPortalPushSubscription,
	unregisterPortalPushSubscription,
} from '../services/portalNotificationService';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { isInsideNativeApp } from './mobileApp';

/**
 * PD-16475 — browser push for the storefront.
 *
 * Reuses the CRM's subscribe flow (WebPushService) with the storefront's own
 * service worker (`public/portal-sw.js`, push-only: it handles `push` and
 * `notificationclick` and does no fetch caching) and its own API. The worker
 * is registered only when the customer turns browser notifications on, never
 * on page load.
 */
export const PORTAL_WEB_PUSH_TARGET: WebPushTargetModel = {
	serviceWorkerPath: '/portal-sw.js',
	getPublicKey: async () =>
		(await getPortalPushPublicKey(usePortalAuthStore.getState().token))
			.publicKey,
	register: async (subscription) => {
		await registerPortalPushSubscription(
			usePortalAuthStore.getState().token,
			subscription,
		);
	},
	unregister: async (endpoint) => {
		await unregisterPortalPushSubscription(
			usePortalAuthStore.getState().token,
			endpoint,
		);
	},
};

/**
 * Whether to offer browser notifications at all. Not inside the storefront's
 * own iOS / Android app: a Capacitor web view has no browser service worker,
 * and app push is a separate, later feature (PD-14302).
 */
export function canOfferBrowserPush(): boolean {
	return !isInsideNativeApp() && WebPushService.isBrowserPushSupported();
}

/** Ask permission and subscribe this browser. False when declined or unavailable. */
export async function enablePortalBrowserPush(): Promise<boolean> {
	if (!canOfferBrowserPush()) {
		return false;
	}
	return WebPushService.enable(PORTAL_WEB_PUSH_TARGET);
}

export async function disablePortalBrowserPush(): Promise<void> {
	if (!canOfferBrowserPush()) {
		return;
	}
	await WebPushService.disable(PORTAL_WEB_PUSH_TARGET);
}

/** Whether this browser currently holds a push subscription for the storefront. */
export async function isPortalBrowserPushOn(): Promise<boolean> {
	if (!canOfferBrowserPush()) {
		return false;
	}
	return WebPushService.isSubscribed();
}
